<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import SalesGoalsAPI from 'dashboard/api/salesGoals';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  funnelId: {
    type: [Number, String],
    default: null,
  },
});

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

const isLoading = ref(false);
const goalsData = ref(null);

const fetchGoals = async () => {
  isLoading.value = true;
  try {
    const { data } = await SalesGoalsAPI.getCurrent();
    goalsData.value = data;
  } catch (error) {
    console.error('Error fetching goals:', error);
  } finally {
    isLoading.value = false;
  }
};

const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const goals = computed(() => goalsData.value?.sales_goals || []);

const teamGoals = computed(() => goals.value.filter(g => !g.user));
const individualGoals = computed(() => goals.value.filter(g => g.user));

const statusConfig = {
  achieved: {
    color: 'bg-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-400',
    icon: 'i-lucide-trophy',
    label: 'DEAL_REPORTS.GOALS.ACHIEVED',
  },
  on_track: {
    color: 'bg-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-700 dark:text-blue-400',
    icon: 'i-lucide-trending-up',
    label: 'DEAL_REPORTS.GOALS.ON_TRACK',
  },
  at_risk: {
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    textColor: 'text-yellow-700 dark:text-yellow-400',
    icon: 'i-lucide-alert-circle',
    label: 'DEAL_REPORTS.GOALS.AT_RISK',
  },
  behind: {
    color: 'bg-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    textColor: 'text-red-700 dark:text-red-400',
    icon: 'i-lucide-trending-down',
    label: 'DEAL_REPORTS.GOALS.BEHIND',
  },
};

const getStatusConfig = status => statusConfig[status] || statusConfig.on_track;

const getProgressColor = progress => {
  if (progress >= 100) return 'bg-green-500';
  if (progress >= 80) return 'bg-blue-500';
  if (progress >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

watch(() => props.funnelId, fetchGoals);

onMounted(fetchGoals);
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ t('DEAL_REPORTS.GOALS.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('DEAL_REPORTS.GOALS.SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center h-48">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
      />
    </div>

    <!-- Content -->
    <div v-else-if="goals.length > 0" class="space-y-6">
      <!-- Team Goals -->
      <div v-if="teamGoals.length > 0">
        <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          {{ t('DEAL_REPORTS.GOALS.TEAM_GOALS') }}
        </h4>
        <div class="space-y-3">
          <div
            v-for="goal in teamGoals"
            :key="goal.id"
            class="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-2">
                <span
                  :class="[
                    getStatusConfig(goal.status).icon,
                    getStatusConfig(goal.status).textColor,
                  ]"
                />
                <span class="font-medium text-slate-900 dark:text-white">
                  {{ goal.name }}
                </span>
              </div>
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="[
                  getStatusConfig(goal.status).bgColor,
                  getStatusConfig(goal.status).textColor,
                ]"
              >
                {{ t(getStatusConfig(goal.status).label) }}
              </span>
            </div>

            <!-- Progress bar -->
            <div class="mb-2">
              <div
                class="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1"
              >
                <span>{{ formatCurrency(goal.current_value) }}</span>
                <span>{{ formatCurrency(goal.target_value) }}</span>
              </div>
              <div
                class="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden"
              >
                <div
                  class="h-full rounded-full transition-all duration-500"
                  :class="getProgressColor(goal.progress.value)"
                  :style="{ width: `${Math.min(goal.progress.value, 100)}%` }"
                />
              </div>
            </div>

            <div
              class="flex justify-between text-xs text-slate-500 dark:text-slate-400"
            >
              <span>{{ goal.progress.value }}%
                {{ t('DEAL_REPORTS.GOALS.COMPLETE') }}</span>
              <span>{{ goal.days_remaining }}
                {{ t('DEAL_REPORTS.GOALS.DAYS_LEFT') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Individual Goals -->
      <div v-if="individualGoals.length > 0">
        <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          {{ t('DEAL_REPORTS.GOALS.INDIVIDUAL_GOALS') }}
        </h4>
        <div class="space-y-3">
          <div
            v-for="goal in individualGoals"
            :key="goal.id"
            class="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <Avatar
                  v-if="goal.user"
                  :src="goal.user.avatar_url"
                  :name="goal.user.name"
                  :size="32"
                  rounded-full
                />
                <div>
                  <p class="font-medium text-slate-900 dark:text-white">
                    {{ goal.user?.name || goal.name }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ goal.name }}
                  </p>
                </div>
              </div>
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="[
                  getStatusConfig(goal.status).bgColor,
                  getStatusConfig(goal.status).textColor,
                ]"
              >
                {{ t(getStatusConfig(goal.status).label) }}
              </span>
            </div>

            <!-- Progress bar -->
            <div class="mb-2">
              <div
                class="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1"
              >
                <span>{{ formatCurrency(goal.current_value) }}</span>
                <span>{{ formatCurrency(goal.target_value) }}</span>
              </div>
              <div
                class="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden"
              >
                <div
                  class="h-full rounded-full transition-all duration-500"
                  :class="getProgressColor(goal.progress.value)"
                  :style="{ width: `${Math.min(goal.progress.value, 100)}%` }"
                />
              </div>
            </div>

            <div
              class="flex justify-between text-xs text-slate-500 dark:text-slate-400"
            >
              <span>{{ goal.progress.value }}%
                {{ t('DEAL_REPORTS.GOALS.COMPLETE') }}</span>
              <span>{{ goal.days_remaining }}
                {{ t('DEAL_REPORTS.GOALS.DAYS_LEFT') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center h-48 text-center"
    >
      <span
        class="i-lucide-target text-4xl text-slate-300 dark:text-slate-600 mb-3"
      />
      <p class="text-slate-500 dark:text-slate-400 mb-2">
        {{ t('DEAL_REPORTS.GOALS.NO_GOALS') }}
      </p>
      <p class="text-xs text-slate-400 dark:text-slate-500">
        {{ t('DEAL_REPORTS.GOALS.CREATE_HINT') }}
      </p>
    </div>
  </div>
</template>
